// SUMMARY — cash breakdown (subtotal / tax / total) AND the estimated
// monthly payment, both always shown (there's no Cash/Finance toggle; the
// machine is dual-priced and accessories/tables are cash-only).
//
// Double-gated:
//   1. Customer hasn't entered full delivery address → all rows show "—"
//   2. Customer hasn't clicked Calculate yet → all rows show "—"
//
// The second gate is the lead-capture lever — we don't want a customer
// seeing their total and bailing before we've upserted their HubSpot
// contact. Once they click Calculate (which fires lead-capture + the
// preview together), the total reveals and stays live as they tweak the
// address. Config changes wipe `calculated` back to false (see store
// reducer's CONFIG_MUTATING_ACTIONS), so each iteration records a fresh
// HubSpot note when re-Calculated.

import { useEffect, useState } from "react";

import { SectionFrame } from "./SectionFrame";
import {
  amortizedMonthlyCents,
  formatDollars,
  formatDollarsAndCents,
  formatMonthly,
} from "../../format";
import {
  CREDIT_APRS,
  CREDIT_TIER_LABELS,
  useConfigurator,
} from "../../store";
import type { CreditTier } from "../../store";
import { PrequalifyModal } from "../PrequalifyModal";
import { TradeInModal } from "../TradeInModal";

export function SummarySection() {
  const {
    state,
    selectedSubtotalCents,
    tradeInCredits,
    hasShippingAddress,
    fetchPreview,
  } = useConfigurator();
  const {
    preview,
    previewLoading,
    previewError,
    customer,
    calculated,
    financing,
    employee,
  } = state;

  const [editingFinancing, setEditingFinancing] = useState(false);
  const [prequalifyOpen, setPrequalifyOpen] = useState(false);
  const [tradeInOpen, setTradeInOpen] = useState(false);

  // Re-fetch the preview when the customer edits their delivery address
  // after Calculate (a new ZIP can shift the tax rate). Config changes
  // (machine/accessory/table/existing-system) don't reach here — the
  // reducer flips `calculated` back to false on those, which both hides
  // the totals and forces the customer to re-click Calculate, which
  // records a fresh HubSpot note for each iteration.
  useEffect(() => {
    if (!calculated || !hasShippingAddress) return;
    const handle = setTimeout(() => fetchPreview(), 400);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    calculated,
    hasShippingAddress,
    customer.zip,
    customer.state,
    customer.city,
    customer.address,
  ]);

  const machineSelected = Boolean(state.selectedMachineId);
  const ready =
    calculated &&
    machineSelected &&
    hasShippingAddress &&
    preview &&
    !previewLoading;

  // Two pre-tax discounts, both off the subtotal before tax (we recompute tax on
  // the net so the on-screen total + monthly match the discounted HubSpot quote):
  //   • the auto-apply promo, computed server-side (preview.discount_cents);
  //   • the employee's manual/trade-in discount (employee mode only).
  const autoPromoCents = preview?.discount_cents ?? 0;
  const empDiscountCents =
    employee.active && employee.discountType && preview
      ? employee.discountType === "percent"
        ? Math.round((preview.subtotal_cents * (employee.discountValue || 0)) / 100)
        : Math.min(
            Math.round((employee.discountValue || 0) * 100),
            preview.subtotal_cents,
          )
      : 0;
  const discountCents = autoPromoCents + empDiscountCents;
  // Retrofit trade-in credits (Breeze-track / old controller), as the backend
  // priced them; the local derivation covers a preview from an older backend.
  const tradeIns = preview?.trade_ins ?? tradeInCredits;
  const tradeInTotalCents = tradeIns.reduce((sum, t) => sum + t.cents, 0);
  // Never deduct past the subtotal (e.g. a rep's 100% discount + credits) —
  // the backend caps the quote's credits the same way.
  const deductionCents = Math.min(
    discountCents + tradeInTotalCents,
    preview?.subtotal_cents ?? Infinity,
  );
  const discountLabel =
    autoPromoCents > 0 && empDiscountCents > 0
      ? "Discounts"
      : autoPromoCents > 0
        ? preview?.discount_label || "Promo"
        : employee.discountLabel.trim() || "Discount";
  const effectiveTaxCents = preview
    ? deductionCents
      ? Math.round((preview.subtotal_cents - deductionCents) * preview.tax_rate)
      : preview.tax_cents
    : 0;
  const effectiveTotalCents = preview
    ? preview.subtotal_cents - deductionCents + effectiveTaxCents
    : selectedSubtotalCents;

  // Monthly math runs on the cash total (subtotal − discount + tax), minus the
  // down payment the customer says they'll bring. Calculated even when
  // not "ready" so the Edit Financing panel can preview the impact of
  // changes; the UI just doesn't surface it until ready.
  const cashTotalCents = effectiveTotalCents;
  const financedPrincipalCents = Math.max(
    0,
    cashTotalCents - financing.downPaymentCents,
  );
  const apr = CREDIT_APRS[financing.creditTier];
  const monthlyCents = amortizedMonthlyCents(
    financedPrincipalCents,
    apr,
    financing.termMonths,
  );

  // Don't show "Your total" (or any subtotal/tax/total rows) until the
  // customer clicks Calculate. Before that the whole section is hidden — the
  // always-visible Calculate button carries its own disabled-reason hint
  // ("Choose a machine first", "Enter your delivery address", …), so no
  // guidance is lost. A config change flips `calculated` back to false (see
  // the reducer's CONFIG_MUTATING_ACTIONS), which re-hides the section until
  // the customer re-Calculates. All hooks above run unconditionally so this
  // early return is safe.
  if (!calculated) return null;

  return (
    <SectionFrame number={5} label="Summary" title="Your total">
      <div className="flex flex-col">
        {ready ? (
          <>
            {/* Cash breakdown first, then the estimated monthly underneath —
                both always shown (no Cash/Finance toggle anymore). */}
            <CashRows
              subtotalCents={selectedSubtotalCents}
              discountCents={discountCents}
              discountLabel={discountLabel}
              tradeIns={tradeIns}
              taxRate={preview!.tax_rate}
              taxCents={effectiveTaxCents}
              taxLabelOverride={preview!.tax_label ?? null}
              totalCents={effectiveTotalCents}
            />
            <MonthlyRow
              monthlyCents={monthlyCents}
              financing={financing}
              onEditFinancing={() => setEditingFinancing((v) => !v)}
              editing={editingFinancing}
            />
          </>
        ) : (
          <>
            <GatedRow label="Subtotal" />
            <GatedRow label="Sales Tax" />
            <div className="my-2 border-t border-dashed border-line" />
            <GatedRow label="Total" emphasized />
            <GatedRow label="Est. monthly payment" emphasized />
            {/* The section only renders post-Calculate, so machine + address
                are already present — the only non-ready states left are the
                tax re-fetch (after an address edit) and a preview error. */}
            {previewLoading && <Note tone="muted">Calculating tax…</Note>}
            {previewError && <Note tone="error">{previewError}</Note>}
          </>
        )}
      </div>

      {/* Edit-financing panel expands inline below the totals when the
          customer clicks the "Edit Financing" link next to the monthly. */}
      {editingFinancing && (
        <EditFinancingPanel onClose={() => setEditingFinancing(false)} />
      )}

      {/* Trade-In and Prequalified lead-magnet CTAs sit under the totals.
          They open HubSpot-form modals. */}
      {ready && (
        <div className="mt-4 flex flex-col gap-2 sm:flex-row sm:gap-3">
          <SecondaryButton onClick={() => setTradeInOpen(true)}>
            Get Trade-In Value
          </SecondaryButton>
          <SecondaryButton onClick={() => setPrequalifyOpen(true)}>
            Get Prequalified
          </SecondaryButton>
        </div>
      )}

      <PrequalifyModal
        open={prequalifyOpen}
        onClose={() => setPrequalifyOpen(false)}
      />
      <TradeInModal
        open={tradeInOpen}
        onClose={() => setTradeInOpen(false)}
      />
    </SectionFrame>
  );
}

function CashRows({
  subtotalCents,
  discountCents = 0,
  discountLabel = "Discount",
  tradeIns = [],
  taxRate,
  taxCents,
  taxLabelOverride,
  totalCents,
}: {
  subtotalCents: number;
  /** Combined pre-tax discount (auto-promo + employee); shows a row when > 0. */
  discountCents?: number;
  /** Row label for the discount (promo label, "Discount", or "Discounts"). */
  discountLabel?: string;
  /** Retrofit trade-in credits — one row each, after the discount row. */
  tradeIns?: { label: string; cents: number }[];
  taxRate: number;
  taxCents: number;
  /** When the backend signals an international destination it sends
   *  e.g. "Local Taxes to be Added" — we render that as the row label
   *  in place of the default "Sales Tax (X%)" and skip the % suffix. */
  taxLabelOverride: string | null;
  totalCents: number;
}) {
  const taxLabel = taxLabelOverride
    ? taxLabelOverride
    : `Sales Tax (${(taxRate * 100).toFixed(3)}%)`;
  return (
    <>
      <Row label="Subtotal" value={formatDollarsAndCents(subtotalCents)} />
      {discountCents > 0 && (
        <Row
          label={discountLabel}
          value={`−${formatDollarsAndCents(discountCents)}`}
        />
      )}
      {tradeIns.map((t) => (
        <Row
          key={t.label}
          label={t.label}
          value={`−${formatDollarsAndCents(t.cents)}`}
        />
      ))}
      <Row label={taxLabel} value={formatDollarsAndCents(taxCents)} />
      <div className="my-2 border-t border-dashed border-line" />
      <Row label="Total" value={formatDollarsAndCents(totalCents)} emphasized />
    </>
  );
}

function MonthlyRow({
  monthlyCents,
  financing,
  onEditFinancing,
  editing,
}: {
  monthlyCents: number;
  financing: { creditTier: CreditTier; termMonths: number; downPaymentCents: number };
  onEditFinancing: () => void;
  editing: boolean;
}) {
  return (
    <>
      <div className="mt-2 flex items-baseline justify-between border-t border-dashed border-line pt-2">
        <span className="text-[14px] font-semibold uppercase tracking-tag text-steel">
          Est. monthly payment
        </span>
        <span className="font-display text-2xl font-extrabold text-ink">
          {formatMonthly(monthlyCents)}
        </span>
      </div>
      <p className="mt-1 font-sans text-[14px] text-steel">
        {formatDollars(financing.downPaymentCents)} down,{" "}
        {financing.termMonths} mo{" "}
        <button
          type="button"
          onClick={onEditFinancing}
          aria-expanded={editing}
          className="ml-1 font-sans text-[14px] font-semibold text-ink underline-offset-2 hover:underline"
        >
          {editing ? "Done" : "Edit Financing"}
        </button>
      </p>
    </>
  );
}

function EditFinancingPanel({ onClose: _onClose }: { onClose: () => void }) {
  const { state, updateFinancing } = useConfigurator();
  const { creditTier, termMonths, downPaymentCents } = state.financing;

  return (
    <div className="mt-4 border-t border-dashed border-line pt-4">
      <p className="mb-3 text-[14px] font-semibold uppercase tracking-tag text-steel">
        Financing assumptions
      </p>

      {/* Credit tier — button group with aria-pressed. Avoids the
          browser's autoscroll-to-focused-radio behavior that hijacked
          the page scroll position when these were native radios. */}
      <div className="mb-4">
        <p className="mb-2 font-sans text-[14px] font-semibold text-ink">
          Estimated credit
        </p>
        <div role="group" aria-label="Estimated credit" className="grid grid-cols-2 gap-2">
          {(Object.keys(CREDIT_APRS) as CreditTier[]).map((tier) => (
            <button
              key={tier}
              type="button"
              aria-pressed={creditTier === tier}
              onClick={() => updateFinancing({ creditTier: tier })}
              className={
                "flex cursor-pointer items-center gap-2 border px-3 py-2 text-left transition " +
                (creditTier === tier
                  ? "border-ink bg-mist"
                  : "border-line hover:border-steel")
              }
            >
              <span
                aria-hidden
                className={
                  "flex h-4 w-4 flex-none items-center justify-center rounded-full border-2 " +
                  (creditTier === tier ? "border-ink" : "border-line")
                }
              >
                {creditTier === tier && (
                  <span className="h-2 w-2 rounded-full bg-ink" />
                )}
              </span>
              <span className="flex-1 font-sans text-[14px] font-semibold text-ink">
                {CREDIT_TIER_LABELS[tier]}
              </span>
            </button>
          ))}
        </div>
      </div>

      {/* Term — button group with aria-pressed. Same autoscroll-avoidance
          as credit tier above (was previously a label/sr-only radio combo
          that triggered browser autoscroll-to-focused-input on click,
          jumping the page way down — looked like a crash, wasn't one). */}
      <div className="mb-4">
        <p className="mb-2 font-sans text-[14px] font-semibold text-ink">
          Loan term
        </p>
        <div role="group" aria-label="Loan term" className="flex gap-2">
          {([60, 72, 84] as const).map((months) => (
            <button
              key={months}
              type="button"
              aria-pressed={termMonths === months}
              onClick={() => updateFinancing({ termMonths: months })}
              className={
                "flex-1 cursor-pointer border px-3 py-2 text-center font-sans text-[14px] transition " +
                (termMonths === months
                  ? "border-ink bg-ink text-white"
                  : "border-line text-ink hover:border-steel")
              }
            >
              {months} mo
            </button>
          ))}
        </div>
      </div>

      {/* Down payment — number input with $1,000 floor (the configurator
          deposit minimum). */}
      <fieldset>
        <legend className="mb-2 font-sans text-[14px] font-semibold text-ink">
          Down payment
        </legend>
        <div className="flex items-center gap-2">
          <span className="font-sans text-[15px] text-ink">$</span>
          <input
            type="number"
            min={1000}
            step={500}
            value={Math.round(downPaymentCents / 100)}
            onChange={(e) =>
              updateFinancing({
                downPaymentCents: Math.round(
                  Number(e.target.value || 0) * 100,
                ),
              })
            }
            className="w-32 border border-line bg-white px-3 py-2 font-sans text-[15px] text-ink focus:border-ink focus:outline-none"
          />
          <span className="font-sans text-[14px] text-steel">
            ($1,000 minimum, matches deposit)
          </span>
        </div>
      </fieldset>
    </div>
  );
}

function Row({
  label,
  value,
  emphasized = false,
}: {
  label: string;
  value: string;
  emphasized?: boolean;
}) {
  return (
    <div
      className={
        "flex items-baseline justify-between py-1 " +
        (emphasized
          ? "font-display text-2xl font-extrabold text-ink"
          : "font-sans text-sm text-steel")
      }
    >
      <span
        className={
          emphasized ? "" : "text-[14px] font-semibold uppercase tracking-tag"
        }
      >
        {label}
      </span>
      <span>{value}</span>
    </div>
  );
}

function GatedRow({
  label,
  emphasized = false,
}: {
  label: string;
  emphasized?: boolean;
}) {
  return (
    <div
      className={
        "flex items-baseline justify-between py-1 " +
        (emphasized
          ? "font-display text-2xl font-extrabold text-steel"
          : "font-sans text-sm text-steel")
      }
    >
      <span
        className={
          emphasized ? "" : "text-[14px] font-semibold uppercase tracking-tag"
        }
      >
        {label}
      </span>
      <span>—</span>
    </div>
  );
}

function Note({
  tone,
  children,
}: {
  tone: "muted" | "error";
  children: React.ReactNode;
}) {
  return (
    <p
      className={
        "mt-3 border-l-[3px] pl-4 py-1 text-[15px] leading-relaxed " +
        (tone === "error"
          ? "border-red-400 font-sans text-red-700"
          : "border-amber font-serif italic text-graphite")
      }
    >
      {children}
    </p>
  );
}

function SecondaryButton({
  onClick,
  children,
}: {
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex-1 border border-line bg-white px-4 py-2.5 font-sans text-[14px] font-semibold text-ink transition hover:border-ink hover:bg-mist"
    >
      {children}
    </button>
  );
}
